import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import { V4_CHIP_DEFAULT, V4_DIALOG_CONTENT, V4_OUTLINED_DEFAULT, V4_TABLE } from '../../theme/v4Compat';
import { ExpandMore as ExpandMoreIcon, Help as HelpIcon, GetApp as GetAppIcon } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { extractApiErrorMessage, notifications } from '../../utils';
import ImportAnimalsDialog from './components/ImportAnimalsDialog';
import ConvertCsvDialog from './components/ConvertCsvDialog';
import { ApiService } from '../../hooks';
import { Loader } from '../../elements';
import { LoaderSize } from '../../constants/constants';
import ImportResults from './components/ImportResults';
import {
  HealthSystemDomainImportResultImportDataFromCsvFileResult,
  HealthSystemDomainImportResultImportDocumentationResponse,
  HealthSystemDomainImportResultCsvExampleFormatsResponse
} from '../../api/HealthSystemApi';

const DataImport: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const { t } = useTranslation();

  const apiService = new ApiService();

  const [isBusy, setIsBusy] = useState(false);

  // State for new API data
  const [documentation, setDocumentation] = useState<HealthSystemDomainImportResultImportDocumentationResponse | null>(
    null
  );
  const [csvFormats, setCsvFormats] = useState<HealthSystemDomainImportResultCsvExampleFormatsResponse | null>(null);
  const [importResult, setImportResult] = useState<HealthSystemDomainImportResultImportDataFromCsvFileResult | null>(
    null
  );

  // Load documentation and CSV formats on component mount
  useEffect(() => {
    const loadDocumentation = async () => {
      try {
        const [docResponse, formatsResponse] = await Promise.all([
          apiService.api.import.importGetImportDocumentation(),
          apiService.api.import.importGetCsvExampleFormats()
        ]);
        setDocumentation(docResponse.data);
        setCsvFormats(formatsResponse.data);
      } catch (error) {
        console.error('Failed to load documentation:', error);
        notifications.error(t('f-import-documentation-load-error'));
      }
    };

    loadDocumentation();
  }, [t]);

  const handleTemplateDownload = async (format: string) => {
    try {
      setIsBusy(true);

      const response = await apiService.api.import.importDownloadCsvExample({ format });

      let blob: Blob;
      const headers: any = response.headers;

      // If this is a Fetch Response (has .blob method)
      if (response.blob) {
        blob = await response.blob();
      }

      // Extract filename from Content-Disposition if available
      const contentDisposition = headers?.get?.('content-disposition') || headers?.['content-disposition'];
      const fileNameMatch = contentDisposition?.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)/);
      const fileName = fileNameMatch ? fileNameMatch[1] : `animal_import_${format}_example.csv`;

      const url = window.URL.createObjectURL(blob!);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      notifications.success(t('f-import-template-download-success', { fileName }));
    } catch (error: any) {
      notifications.error(t('f-import-template-download-error', { error: error.message }));
    } finally {
      setIsBusy(false);
    }
  };

  const handleValidate = (file: File | null) => {
    if (!file) return;

    setIsBusy(true);
    setImportFile(file); // Store file for later import
    setShowDocumentation(false); // Collapse the guide to show results

    apiService.api.import
      .importValidateAnimals({ file }) // Use validation endpoint first
      .then((result) => {
        setDialogOpen(false);
        setImportResult(result.data);

        if (result.data.isSuccess && result.data.errors.length === 0) {
          notifications.success(t('f-import-validation-success', { validRows: result.data.validRows }));
        } else {
          notifications.warning(t('f-import-validation-error', { errorCount: result.data.errors.length }));
        }
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  const handleImport = () => {
    if (!importResult || !importResult.isSuccess || importResult.errors.length > 0) {
      notifications.warning(t('f-import-fix-errors-warning'));
      return;
    }

    if (!importFile) {
      notifications.error(t('f-data-import-error', { error: 'No file available for import' }));
      return;
    }

    // Show confirmation dialog
    setConfirmDialogOpen(true);
  };

  const handleConfirmImport = async () => {
    if (!importFile || !importResult) return;

    setConfirmDialogOpen(false);
    setIsBusy(true);

    try {
      const result = await apiService.api.import.importImportAnimals({ file: importFile });

      if (result.data.isSuccess) {
        notifications.success(t('f-import-success', { created: result.data.createdCount }));
        // Update the import result with the new data
        setImportResult(result.data);
      } else {
        notifications.error(t('f-import-error', { error: result.data.errorMessage || 'Unknown error occurred' }));
      }
    } catch (error: any) {
      notifications.error(t('f-import-error', { error: error.toString() }));
    } finally {
      setIsBusy(false);
    }
  };

  const handleCancelImport = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <Grid container spacing={3}>
      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      {/* Main Import Section */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('f-import-title')}
            </Typography>

            <Typography variant="body2" gutterBottom>
              {t('f-import-description')}
            </Typography>

            <Box style={{ marginTop: 16, marginBottom: 16 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setDialogOpen(true)}
                style={{ marginRight: 16 }}
              >
                {t('f-import-upload-validate-btn')}
              </Button>

              <Button
                variant="outlined"
                color="primary"
                onClick={() => setConvertDialogOpen(true)}
                style={{ marginRight: 16 }}
              >
                {t('f-import-convert-open-btn')}
              </Button>

              {importResult && importResult.isSuccess && importResult.errors.length === 0 && (
                <Button variant="contained" color="secondary" onClick={handleImport}>
                  {t('f-import-data-btn')}
                </Button>
              )}
            </Box>

            <Typography variant="body2" gutterBottom>
              {t('f-import-template-download-text')}
            </Typography>

            <Box style={{ marginBottom: 16 }}>
              {csvFormats?.formats.map((format) => (
                <Button
                  key={format.key}
                  variant="outlined"
                  size="small"
                  startIcon={<GetAppIcon />}
                  onClick={() => handleTemplateDownload(format.key)}
                  style={{ marginRight: 8, marginBottom: 8 }}
                  title={format.description}
                  color="inherit"
                  sx={V4_OUTLINED_DEFAULT}
                >
                  {format.name}
                </Button>
              ))}
            </Box>

            <Alert severity="info">{t('f-import-first-time-user-info')}</Alert>
          </CardContent>
        </Card>
      </Grid>

      {/* Documentation Section */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Box style={{ marginBottom: 16 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <HelpIcon color="primary" style={{ marginRight: 8 }} />
              <Typography variant="h6">{t('f-import-guide-title')}</Typography>
            </Box>

            <Button
              variant="outlined"
              fullWidth
              onClick={() => setShowDocumentation(!showDocumentation)}
              endIcon={<ExpandMoreIcon />}
              color="inherit"
              sx={V4_OUTLINED_DEFAULT}
            >
              {showDocumentation ? t('f-import-guide-hide') : t('f-import-guide-show')}
            </Button>
          </CardContent>
        </Card>
      </Grid>

      {/* Expandable Documentation */}
      {showDocumentation && documentation && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {documentation.title}
              </Typography>

              <Typography variant="body2" sx={{ mb: 2 }}>
                {documentation.description}
              </Typography>

              {/* Required Columns */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">{t('f-import-required-columns')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small" sx={V4_TABLE}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('f-import-column-name')}</TableCell>
                        <TableCell>{t('f-import-column-description')}</TableCell>
                        <TableCell>{t('f-import-column-format')}</TableCell>
                        <TableCell>{t('f-import-column-examples')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documentation.requiredColumns.map((col) => (
                        <TableRow key={col.name}>
                          <TableCell>
                            <strong>{col.name}</strong>
                          </TableCell>
                          <TableCell>{col.description}</TableCell>
                          <TableCell>{col.format}</TableCell>
                          <TableCell>
                            {col.examples.map((example, idx) => (
                              <Chip
                                key={idx}
                                label={example}
                                size="small"
                                style={{ marginRight: 4, marginBottom: 4 }}
                                sx={V4_CHIP_DEFAULT}
                              />
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>

              <Divider style={{ margin: '16px 0' }} />

              {/* Optional Columns */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">{t('f-import-optional-columns')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small" sx={V4_TABLE}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('f-import-column-name')}</TableCell>
                        <TableCell>{t('f-import-column-description')}</TableCell>
                        <TableCell>{t('f-import-column-format')}</TableCell>
                        <TableCell>{t('f-import-column-examples')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documentation.optionalColumns.map((col) => (
                        <TableRow key={col.name}>
                          <TableCell>{col.name}</TableCell>
                          <TableCell>{col.description}</TableCell>
                          <TableCell>{col.format}</TableCell>
                          <TableCell>
                            {col.examples.map((example, idx) => (
                              <Chip
                                key={idx}
                                label={example}
                                size="small"
                                style={{ marginRight: 4, marginBottom: 4 }}
                                sx={V4_CHIP_DEFAULT}
                              />
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>

              <Divider style={{ margin: '16px 0' }} />

              {/* Accepted Values */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">{t('f-import-accepted-values')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {documentation.acceptedValues.map((group) => (
                      <Grid item xs={12} md={6} key={group.field}>
                        <Card variant="outlined" style={{ padding: 16 }}>
                          <Typography variant="subtitle2" gutterBottom color="primary">
                            {group.field}
                          </Typography>
                          <Grid container spacing={1}>
                            {group.values.map((value, idx) => (
                              <Grid item xs={12} key={idx}>
                                <Box
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: 4,
                                    marginBottom: 4,
                                    border: '1px solid #e9ecef'
                                  }}
                                >
                                  <Typography variant="body2" style={{ fontWeight: 'bold', color: '#495057' }}>
                                    {value.input}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    → {value.mapsTo} ({value.numericValue})
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Divider style={{ margin: '16px 0' }} />

              {/* Validation Rules */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">{t('f-import-validation-rules')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {documentation.validationRules.map((category) => (
                      <Grid item xs={12} key={category.category}>
                        <Card variant="outlined" style={{ padding: 16, marginBottom: 8 }}>
                          <Typography variant="subtitle2" gutterBottom color="primary">
                            {category.category}
                          </Typography>
                          <Grid container spacing={1}>
                            {category.rules.map((rule, idx) => (
                              <Grid item xs={12} key={idx}>
                                <Box
                                  style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffeaa7',
                                    borderRadius: 4,
                                    marginBottom: 4
                                  }}
                                >
                                  <Typography variant="body2">{rule}</Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Divider style={{ margin: '16px 0' }} />

              {/* Examples */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">{t('f-import-csv-examples')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {documentation.examples.map((example) => (
                      <Grid item xs={12} key={example.title}>
                        <Card variant="outlined" style={{ padding: 16, marginBottom: 8 }}>
                          <Typography variant="subtitle2" gutterBottom color="primary">
                            {example.title}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            {example.description}
                          </Typography>
                          <Box
                            component="pre"
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: 12,
                              fontSize: '0.75rem',
                              overflow: 'auto',
                              borderRadius: 4,
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {example.content}
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Divider style={{ margin: '16px 0' }} />

              {/* Best Practices */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">{t('f-import-best-practices')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ul>
                    {documentation.bestPractices.map((practice, idx) => (
                      <li key={idx}>
                        <Typography variant="body2">{practice}</Typography>
                      </li>
                    ))}
                  </ul>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Import Results */}
      {importResult && (
        <Grid item xs={12}>
          <ImportResults result={importResult} onImportClick={handleImport} />
        </Grid>
      )}

      {/* Import Dialog */}
      <ImportAnimalsDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onImport={handleValidate} />

      {/* Convert CSV Dialog (map an arbitrary CSV into the import schema, then validate) */}
      <ConvertCsvDialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        onConvert={handleValidate}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelImport}>
        <DialogTitle>{t('f-import-confirm-title')}</DialogTitle>
        <DialogContent sx={V4_DIALOG_CONTENT}>
          <DialogContentText>
            {t('f-import-confirm-message', { count: importResult?.validRows || 0 })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelImport} color="primary">
            {t('f-import-confirm-no')}
          </Button>
          <Button onClick={handleConfirmImport} color="primary" variant="contained" autoFocus>
            {t('f-import-confirm-yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default DataImport;
